import { useMemo, useCallback, useState, useRef } from 'react';
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCorners,
    type DragEndEvent,
    type DragStartEvent,
    DragOverlay,
    type CollisionDetection,
    pointerWithin,
    rectIntersection,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { CheckSquare, Loader2, AlertCircle, Plus, Settings } from 'lucide-react';
import type {
    ColumnDef,
    CreateColumnBody,
    CustomField,
    SavedView,
    Task,
    UpdateColumnBody,
} from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
    useCreateColumn,
    useDeleteColumn,
    useReorderColumns,
    useUpdateColumn,
    useUpdateSavedView,
} from '@/hooks/use-views';
import { KanbanColumn } from './kanban-column';
import { KanbanCard } from './kanban-card';
import { AddColumnDialog } from './add-column-dialog';
import { EditColumnDialog } from './edit-column-dialog';
import { KanbanSettingsPanel } from './kanban-settings-panel';
import {
    columnToTaskUpdate,
    resolveKanbanColumns,
    taskMatchesColumn,
    type RenderableColumn,
} from './kanban-status-config';

// ============================================
// KANBAN VIEW
// ============================================
//
// t4+: drag a card onto any column whose filterRules collapse to a single
// field update (status, priority, or customFieldValues) and we PATCH the
// existing /api/tasks/:id endpoint. Drag the column header to reorder —
// the column's id travels with the drag and the new order is persisted
// via the /columns/reorder endpoint. All column CRUD goes through the
// hooks in /hooks/use-views.ts so the React Query cache stays coherent
// across the sidebar and the board.

export interface KanbanViewProps {
    projectId: string;
    tasks: Task[];
    isLoading: boolean;
    error?: Error | null;
    customFields: CustomField[];
    visibleCardFieldIds: string[];
    /** Active Kanban view — drives what columns to render and the CRUD endpoints. */
    activeView?: SavedView | null;
    /**
     * t10: when a column CRUD response carries `viewIdChanged` (auto-clone
     * fork from t1), the parent must re-key its `activeViewId` state so
     * the derived `activeView` follows the cloned row. Without this the
     * next render would resolve to `null` and the user would have to
     * manually re-pick the view.
     */
    onActiveViewIdChange?: (newViewId: string) => void;
    onCardClick?: (task: Task) => void;
    /** t15: invoked when the user clicks a subtask row in a card's
        subtask popover. The parent (project-detail) wires this to the
        existing task-detail panel handler so the subtask panel opens
        with the same UX as a left-click on a card. */
    onSelectSubtask?: (task: Task) => void;
    /**
     * Move callback. The view derives the right PATCH body from the target
     * column's filterRules; the parent just needs to issue the network call.
     */
    onTaskMoved?: (taskId: string, update: { field: 'status' | 'priority' | 'customFieldValues'; value: unknown }) => Promise<void>;
    onClearFilters?: () => void;
    hasActiveFilters?: boolean;
    canEdit?: boolean;
    projectMembers?: Array<{ userId: string; user: { id: string; name: string } }>;
}

// Heuristic that decides what an "over" target is. Column drops look for
// `column-<id>` ids (from useDroppable); column-reorder drops look for
// the column's bare id (from useSortable). The dual detection keeps card
// drags and column drags working side-by-side without either swallowing
// the other.
function detectActiveDrag(activeType: string | undefined, overData: any): null | { type: 'card' | 'column'; id: string } {
    if (!activeType) return null;
    if (activeType === 'card') {
        if (!overData) return null;
        // Card can be dropped onto a column droppable OR onto another card
        // (sortable). We prefer the column id when present.
        const isColumn = overData?.type === 'column';
        const id = isColumn ? String(overData.columnId) : String(overData?.id ?? '');
        return { type: 'card', id };
    }
    return null;
}

export function KanbanView({
    projectId,
    tasks,
    isLoading,
    error,
    customFields,
    visibleCardFieldIds,
    activeView,
    onActiveViewIdChange,
    onCardClick,
    onSelectSubtask,
    onTaskMoved,
    onClearFilters,
    hasActiveFilters,
    canEdit,
    projectMembers = [],
}: KanbanViewProps) {
    const { t } = useI18n();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // The active view's column config. We always normalize to ColumnDef[]
    // before rendering so a stale payload can't crash the board.
    //
    // R5 fix: split "no view selected" from "user-created empty view".
    // The two paths used to share `null/undefined → fallback to 5
    // defaults` semantics in resolveKanbanColumns, which made
    // user-created empty views (the backend stores
    // `{"filters": {}}` with no kanbanColumns field) render the 5
    // defaults instead of the empty-state CTA. Now:
    //   - `activeView === null` → pass `null` so resolveKanbanColumns
    //     falls back to the 5 defaults (the "All tasks" virtual default).
    //   - `activeView` exists but its config has no / empty
    //     `kanbanColumns` → pass `[]` so resolveKanbanColumns returns
    //     [] and the empty-state CTA can fire (isEmptyColumns below).
    const rawColumns: unknown = activeView === null
        ? null
        : ((activeView?.config?.kanbanColumns as unknown) ?? []);
    const allColumns: RenderableColumn[] = useMemo(
        () => resolveKanbanColumns(rawColumns),
        [rawColumns]
    );
    // Filter out hidden columns for rendering. Hidden columns still exist
    // in the view's config; their tasks simply fall through to "Other".
    const visibleColumns = useMemo(
        () => allColumns.filter((c) => c.visible !== false),
        [allColumns]
    );

    // Bucket tasks per visible column id.
    //
    // t10: OR-semantics across columns. A task that matches multiple
    // columns' filterRules (AND within one column, OR across columns)
    // shows up in ALL matching columns. A task matching NO visible
    // column lands in the synthesized "Other" column — but only when
    // at least one visible column exists (if every column is hidden the
    // task is invisible, matching the spec).
    //
    // R6 fix: when an explicit saved view is active (activeView !== null),
    // tasks that don't match any visible column are HIDDEN, not pushed
    // to "Other". The user opted into a custom view with explicit
    // columns, so anything that doesn't fit is intentionally excluded.
    // They can switch to the unfiltered "All tasks" sidebar entry to see
    // everything. The "All tasks" virtual default (activeView === null)
    // still surfaces unmatched tasks in "Other" so newcomers see the
    // full picture without configuring columns first.
    const { bucketed, otherTasks } = useMemo(() => {
        const map = new Map<string, Task[]>();
        for (const c of visibleColumns) map.set(c.id, []);
        const other: Task[] = [];
        for (const task of tasks) {
            let matchedAny = false;
            for (const col of visibleColumns) {
                if (taskMatchesColumn(task, col, customFields)) {
                    map.get(col.id)!.push(task);
                    matchedAny = true;
                }
            }
            // Only the unfiltered default (activeView === null) gets an
            // "Other" lane — explicit views hide unmatched tasks so the
            // board reflects the user's chosen column set exactly.
            if (!matchedAny && visibleColumns.length > 0 && activeView === null) {
                other.push(task);
            }
        }
        return { bucketed: map, otherTasks: other };
    }, [tasks, visibleColumns, customFields, activeView]);

    // ---------- Active drag ----------
    const [activeDragId, setActiveDragId] = useState<string | null>(null);
    const [activeColumnDragId, setActiveColumnDragId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            // Require a tiny movement before a drag starts so click events
            // still fire on the card (otherwise the title click handler
            // would never run).
            activationConstraint: { distance: 6 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // View-id swap handler: when a non-owner editing a shared/default
    // view triggers the auto-clone from t1, the column-CRUD response
    // carries the new (cloned) view id. We forward it to the parent so
    // its `activeViewId` is re-keyed and the derived `activeView`
    // follows the cloned row on the next render.
    const handleViewIdSwap = useCallback((newViewId: string) => {
        if (!activeView) return;
        // t10: tell the parent to swap its local activeViewId state.
        // The parent's derived activeView useMemo then re-evaluates and
        // picks up the cloned row.
        if (onActiveViewIdChange) {
            onActiveViewIdChange(newViewId);
        }
    }, [activeView, onActiveViewIdChange]);

    // Mutations
    const updateSavedView = useUpdateSavedView(projectId);
    const createColumn = useCreateColumn(projectId, activeView?.id ?? '', handleViewIdSwap);
    const reorderColumns = useReorderColumns(projectId, activeView?.id ?? '', handleViewIdSwap);
    const updateColumn = useUpdateColumn(projectId, activeView?.id ?? '', handleViewIdSwap);
    const deleteColumn = useDeleteColumn(projectId, activeView?.id ?? '', handleViewIdSwap);

    // Persist visibleCardFields immediately on toggle.
    const handleCardFieldsChange = useCallback(
        (next: string[]) => {
            if (!activeView) return;
            updateSavedView.mutate({ viewId: activeView.id, data: { visibleCardFields: next } });
        },
        [activeView, updateSavedView]
    );

    // ---------- Drag handlers ----------
    const handleDragStart = useCallback((event: DragStartEvent) => {
        const type = (event.active.data.current as any)?.type;
        if (type === 'column-handle') {
            setActiveColumnDragId(String(event.active.id));
        } else {
            setActiveDragId(String(event.active.id));
        }
    }, []);

    const handleDragCancel = useCallback(() => {
        setActiveDragId(null);
        setActiveColumnDragId(null);
    }, []);

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const activeData = event.active.data.current as any;
            const overData = event.over?.data.current as any;
            const activeType = activeData?.type;

            // Reset overlay state no matter the outcome.
            setActiveDragId(null);
            setActiveColumnDragId(null);

            if (!event.over) return;

            // ----- Column reorder -----
            if (activeType === 'column-handle') {
                const activeId = String(event.active.id);
                // R6 fix: overId might be either the column's sortable
                // id ("col_xxx") when the drag ends over the header, OR
                // the droppable id ("column-col_xxx") when the drag
                // ends over the column body (the droppable owns that
                // area for card drops). Strip the "column-" prefix
                // before lookup so both drop targets resolve to the
                // visibleColumns id. The previous code assumed the
                // sortable-only path, so dropping anywhere but the
                // header silently returned early via `newIndex === -1`
                // and the user reported "the column reorder resets".
                const overId = String(event.over.id);
                const overColumnId = overId.startsWith('column-')
                    ? overId.slice('column-'.length)
                    : overId;
                if (activeId === overColumnId) return;
                if (!activeView) return;
                const ids = visibleColumns.map((c) => c.id);
                const oldIndex = ids.indexOf(activeId);
                const newIndex = ids.indexOf(overColumnId);
                if (oldIndex === -1 || newIndex === -1) return;
                const nextOrder = arrayMove(ids, oldIndex, newIndex);
                reorderColumns.mutate(
                    { columnIds: nextOrder },
                    {
                        onError: (err) => {
                            toast({
                                title: t('common.error'),
                                description: err instanceof Error ? err.message : t('kanban.errorReorderColumns'),
                                variant: 'destructive',
                            });
                        },
                    }
                );
                return;
            }

            // ----- Card drag -----
            if (activeType === 'card') {
                const taskId = String(activeData.taskId);
                // The drop target id is `column-<id>` from useDroppable. Strip the prefix.
                const rawOverId = String(event.over.id);
                const targetColumnId = rawOverId.startsWith('column-')
                    ? rawOverId.slice('column-'.length)
                    : rawOverId;
                const target = visibleColumns.find((c) => c.id === targetColumnId);
                if (!target) return;

                const task = tasks.find((t) => t.id === taskId);
                if (!task) return;

                // Skip no-op drops: same column already.
                const currentCol = visibleColumns.find((c) => taskMatchesColumn(task, c, customFields));
                if (currentCol?.id === target.id) return;

                // Derive the PATCH body from the target column's filterRules.
                const update = columnToTaskUpdate(target);
                if (!update) {
                    toast({
                        title: t('kanban.cannotDropHere'),
                        description: t('kanban.cannotDropHereDescription'),
                        variant: 'destructive',
                    });
                    return;
                }

                // Optimistic update of the React Query cache for both list
                // pages and any active infinite-query snapshots.
                const queryKeys = queryClient
                    .getQueryCache()
                    .findAll({ queryKey: ['tasks', 'list'] })
                    .map((q) => q.queryKey);

                const snapshot = new Map<string, unknown>();
                for (const key of queryKeys) {
                    snapshot.set(JSON.stringify(key), queryClient.getQueryData(key));
                }

                for (const key of queryKeys) {
                    queryClient.setQueryData(key, (old: any) => {
                        if (!old?.pages) return old;
                        return {
                            ...old,
                            pages: old.pages.map((page: any) => ({
                                ...page,
                                tasks: page.tasks.map((t: Task) =>
                                    t.id === taskId ? { ...t, [update.field]: update.value } : t
                                ),
                            })),
                        };
                    });
                }

                try {
                    if (onTaskMoved) {
                        await onTaskMoved(taskId, update);
                    }
                } catch (err) {
                    for (const [json, data] of snapshot) {
                        queryClient.setQueryData(JSON.parse(json), data);
                    }
                    toast({
                        title: t('common.error'),
                        description: err instanceof Error ? err.message : t('toast.errorUpdatingTasks'),
                        variant: 'destructive',
                    });
                }
                return;
            }
        },
        [
            visibleColumns,
            activeView,
            reorderColumns,
            tasks,
            customFields,
            queryClient,
            onTaskMoved,
            toast,
            t,
        ]
    );

    // Column CRUD wrappers
    const handleCreateColumn = useCallback(
        async (data: CreateColumnBody) => {
            if (!activeView) return;
            try {
                await createColumn.mutateAsync({
                    name: data.name,
                    color: data.color,
                    filterRules: data.filterRules,
                });
                toast({ title: t('kanban.columnCreated'), description: data.name });
            } catch (err) {
                toast({
                    title: t('common.error'),
                    description: err instanceof Error ? err.message : t('kanban.errorCreateColumn'),
                    variant: 'destructive',
                });
            }
        },
        [activeView, createColumn, toast, t]
    );

    // Edit-column dialog state. `null` means the dialog is closed; a
    // ColumnDef opens it pre-filled with that column's values.
    const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
    const editingColumn = useMemo(
        () => (editingColumnId ? allColumns.find((c) => c.id === editingColumnId) || null : null),
        [editingColumnId, allColumns]
    );

    const handleEditColumn = useCallback((columnId: string) => {
        setEditingColumnId(columnId);
    }, []);

    const handleUpdateColumn = useCallback(
        async (data: { name: string; color?: string; filterRules: any[] }) => {
            if (!editingColumn) return;
            try {
                await updateColumn.mutateAsync({
                    colId: editingColumn.id,
                    body: data,
                });
                toast({ title: t('kanban.columnUpdated'), description: data.name });
                setEditingColumnId(null);
            } catch (err) {
                toast({
                    title: t('common.error'),
                    description: err instanceof Error ? err.message : t('kanban.errorUpdateColumn'),
                    variant: 'destructive',
                });
            }
        },
        [editingColumn, updateColumn, toast, t]
    );

    // Hide a column — the column stays in the view's config (with
    // visible=false) but is filtered out of the board. The Columns
    // popover can re-enable it later. issue #7.
    const handleHideColumn = useCallback(
        (columnId: string) => {
            if (!activeView) return;
            updateColumn.mutate(
                { colId: columnId, body: { visible: false } },
                {
                    onError: (err) =>
                        toast({
                            title: t('common.error'),
                            description: err instanceof Error ? err.message : t('kanban.errorUpdateColumn'),
                            variant: 'destructive',
                        }),
                }
            );
        },
        [activeView, updateColumn, toast, t]
    );

    const handleDeleteColumn = useCallback(
        (columnId: string) => {
            if (!activeView) return;
            deleteColumn.mutate(columnId, {
                onSuccess: () => toast({ title: t('kanban.columnDeleted') }),
                onError: (err) =>
                    toast({
                        title: t('common.error'),
                        description: err instanceof Error ? err.message : t('kanban.errorDeleteColumn'),
                        variant: 'destructive',
                    }),
            });
        },
        [activeView, deleteColumn, toast, t]
    );

    const handleToggleVisibility = useCallback(
        (columnId: string, visible: boolean) => {
            if (!activeView) return;
            updateColumn.mutate(
                { colId: columnId, body: { visible } },
                {
                    onError: (err) =>
                        toast({
                            title: t('common.error'),
                            description: err instanceof Error ? err.message : t('kanban.errorUpdateColumn'),
                            variant: 'destructive',
                        }),
                }
            );
        },
        [activeView, updateColumn, toast, t]
    );

    // Toolbar state for the Add Column dialog (lives in the toolbar per
    // t8 issue #1, not at the end of the columns row).
    const [addColumnOpen, setAddColumnOpen] = useState(false);

    // t15 R3: settings cog popover state + anchor ref. The Settings
    // button lives in the toolbar; the popover (rendered below the
    // toolbar) anchors against the ref so it lines up directly under
    // the button — same pattern as CalendarSettingsPanel.
    const [settingsOpen, setSettingsOpen] = useState(false);
    const settingsButtonRef = useRef<HTMLButtonElement | null>(null);

    const activeTask = useMemo(
        () => (activeDragId ? tasks.find((tk) => tk.id === activeDragId) : undefined),
        [activeDragId, tasks]
    );

    // Column-level collision detection. We need a heuristic that lets
    // cards drop onto columns AND lets columns reorder among themselves.
    const cardCollision: CollisionDetection = (args) => {
        // Prefer columns when the pointer is over a column droppable;
        // otherwise fall back to intersect so column reorders work too.
        const pointer = pointerWithin(args);
        if (pointer.length > 0) {
            const overColumn = pointer.find((p) => (args.droppableContainers.find((c) => c.id === p.id)?.data.current as any)?.type === 'column');
            if (overColumn) return [overColumn];
            return pointer;
        }
        return rectIntersection(args);
    };

    // ---------- Render ----------
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <AlertCircle className="h-10 w-10 text-destructive mb-3" />
                <p className="text-sm text-foreground-muted">{error.message || 'Failed to load board'}</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
            </div>
        );
    }

    const hasAnyData = tasks.length > 0 || otherTasks.length > 0;

    // R2: when `activeView` is null (the sidebar's "All tasks" label
    // with no saved view selected), we render the canonical 5 status
    // columns. resolveKanbanColumns already falls back to those defaults
    // when the raw input is null/undefined OR an empty array — but for
    // the empty-array case we ALSO want to show the "Create your first
    // column" CTA (R1) so the user can build out a custom view. So we
    // track the raw input separately: the CTA path needs activeView to
    // exist AND the raw payload to be an explicitly empty array. The
    // `allColumns` derived from resolveKanbanColumns is what the columns
    // row actually renders (5 defaults in the null/undefined case).
    //
    // R1: a brand-new user-created Kanban view starts empty. We detect
    // that with two conditions — (a) the view has no stored columns at
    // all (raw input is an empty array) AND (b) the view is NOT the
    // auto-seeded "All tasks" default that the GET handler still
    // creates with 5 status columns. The auto-seeded default should
    // never hit this branch in practice because the GET handler always
    // populates it, but we keep the name check so a corrupted "All
    // tasks" row never accidentally shows the CTA. The CTA opens the
    // same AddColumnDialog the toolbar button uses; reusing
    // `addColumnOpen` keeps state consistent.
    //
    // R5: `rawColumns` is now always either `null` (no view selected)
    // or an array. The CTA only fires for the array-and-empty case.
    const isAutoSeededDefault =
        !!activeView && activeView.name === t('views.allTasks');
    const isEmptyColumns =
        !!activeView
        && Array.isArray(rawColumns)
        && rawColumns.length === 0
        && !isAutoSeededDefault;

    return (
        <div className="space-y-3">
            {/* Kanban toolbar: t15 R3 collapsed the R1 two-popover
                layout (ColumnsVisibilityPopover + CardFieldsPopover)
                into a single cog-icon Settings button that opens
                KanbanSettingsPanel. The Add column button stays
                inline as a primary action (matches the Calendar/Gantt
                pattern where date-creation stays on the toolbar and
                only view-level config moves into Settings). t8 issue
                #1: the Add column button moved out of the columns row
                into the toolbar so the row stays 100% real columns.
                The Add button only renders when the current view is
                editable. R1: when the view is empty AND not the
                auto-seeded default, the toolbar's cog + Add button
                are useless — we hide them and let the dedicated empty
                state below carry the "Create your first column" CTA
                instead. */}
            {activeView && !isEmptyColumns && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        {canEdit && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => setAddColumnOpen(true)}
                            >
                                <Plus className="h-3.5 w-3.5 mr-1.5" />
                                {t('kanban.addColumn')}
                            </Button>
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 cursor-pointer"
                        onClick={() => setSettingsOpen(true)}
                        aria-label={t('kanban.settings')}
                        title={t('kanban.settings')}
                        ref={settingsButtonRef}
                        data-testid="kanban-settings-button"
                    >
                        <Settings className="h-4 w-4" />
                    </Button>
                </div>
            )}

            {/* R1: AddColumnDialog is rendered OUTSIDE the toolbar's
                conditional so both the toolbar Add button (when there
                are columns) AND the empty-state CTA (when the view is
                empty) can open the same dialog instance. Reusing the
                `addColumnOpen` state keeps the open/close behaviour
                consistent between both entry points. */}
            <AddColumnDialog
                open={addColumnOpen}
                onOpenChange={setAddColumnOpen}
                onSubmit={handleCreateColumn}
                customFields={customFields}
                projectMembers={projectMembers}
            />

            {/* t15 R3: settings cog popover. Anchored to the Settings
                button above so it lines up directly under it. Wraps
                the column visibility + card fields pickers in one
                panel (the two R1 popovers this replaces). Controlled
                by `settingsOpen` so the toolbar button is the sole
                trigger. */}
            <KanbanSettingsPanel
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                columns={allColumns}
                customFields={customFields}
                selectedFieldIds={visibleCardFieldIds}
                onToggleColumn={handleToggleVisibility}
                onToggleCardField={(fieldId) => {
                    const next = visibleCardFieldIds.includes(fieldId)
                        ? visibleCardFieldIds.filter((id) => id !== fieldId)
                        : [...visibleCardFieldIds, fieldId];
                    handleCardFieldsChange(next);
                }}
                anchorRef={settingsButtonRef}
                disabled={!canEdit}
            />

            <DndContext
                sensors={sensors}
                collisionDetection={cardCollision}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
                onDragOver={() => { /* no-op: we only react to dragEnd */ }}
            >
                <SortableContext
                    items={visibleColumns.map((c) => c.id)}
                    strategy={horizontalListSortingStrategy}
                >
                    {/* t6: full-width columns. t8: rows are 100% real
                        columns — the Add button moved to the toolbar
                        above (issue #1). The Other column still
                        renders last when non-empty so unmatched tasks
                        remain visible. R1: when the view has no columns
                        AND isn't the auto-seeded default, we skip the
                        columns row entirely and let the dedicated
                        empty-state CTA below take over the canvas. */}
                    {!isEmptyColumns && (
                        <div className="w-full">
                            <div className="flex flex-col gap-3 overflow-y-auto sm:flex-row sm:overflow-x-auto sm:overflow-y-visible pb-4 items-start">
                                {visibleColumns.map((column) => (
                                    <KanbanColumn
                                        key={column.id}
                                        column={column}
                                        tasks={bucketed.get(column.id) || []}
                                        customFields={customFields}
                                        visibleCardFieldIds={visibleCardFieldIds}
                                        onCardClick={onCardClick}
                                        onSelectSubtask={onSelectSubtask}
                                        onDeleteColumn={canEdit && activeView ? handleDeleteColumn : undefined}
                                        onEditColumn={canEdit && activeView ? handleEditColumn : undefined}
                                        onHideColumn={canEdit && activeView ? handleHideColumn : undefined}
                                        // R13: the default ("All tasks"
                                        // virtual) view's columns are
                                        // derived from the Status enum
                                        // and aren't editable — disable
                                        // the drag handle, the column
                                        // reorder hook, and the
                                        // right-click context menu. The
                                        // existing `showContextMenu`
                                        // / ColumnHeader gating in
                                        // kanban-column.tsx checks
                                        // `!dragDisabled` so flipping
                                        // this flag also hides those
                                        // affordances without a
                                        // separate prop.
                                        dragDisabled={!canEdit || activeView === null}
                                    />
                                ))}
                                {otherTasks.length > 0 && (
                                    <KanbanColumn
                                        column={{ id: 'OTHER', name: 'Other', variant: 'cancelled', visible: true, filterRules: [] }}
                                        tasks={otherTasks}
                                        customFields={customFields}
                                        visibleCardFieldIds={visibleCardFieldIds}
                                        onCardClick={onCardClick}
                                        isOtherColumn
                                    />
                                )}
                                {!hasAnyData && (
                                    <KanbanEmptyState
                                        hasActiveFilters={hasActiveFilters}
                                        onClearFilters={onClearFilters}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </SortableContext>

                <DragOverlay dropAnimation={null}>
                    {/* t8 fix (issue #8b): the overlay copy uses an opaque
                        bg + shadow + ring so the column drop-preview
                        can never bleed through. We also pass overlay=true
                        so KanbanCard skips its draggable wiring (the
                        overlay lives outside the DndContext collision
                        tree). Width matches the source column. */}
                    {activeTask ? (
                        <KanbanCard
                            task={activeTask}
                            customFields={customFields}
                            visibleCardFieldIds={visibleCardFieldIds}
                            overlay
                        />
                    ) : null}
                    {/* Show a ghost column for column-drag overlays too. */}
                    {activeColumnDragId ? (
                        <div className="w-72 shrink-0 bg-white/5 border border-white/10 rounded-xl opacity-80">
                            <header className="px-3 py-2 border-b border-white/10">
                                <span className="text-xs text-foreground-muted">
                                    {allColumns.find((c) => c.id === activeColumnDragId)?.name || ''}
                                </span>
                            </header>
                            <div className="min-h-[120px] p-2" />
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* R1: dedicated empty state for a brand-new empty Kanban
                view. Replaces the columns row entirely (above). The CTA
                reuses the same `addColumnOpen` state as the toolbar
                Add button so the dialog wiring is identical. Only
                renders when the user can actually edit — read-only
                users see the columns-row empty state instead. */}
            {isEmptyColumns && canEdit && (
                <KanbanNoColumnsState onCreate={() => setAddColumnOpen(true)} />
            )}
            {isEmptyColumns && !canEdit && (
                <KanbanEmptyState />
            )}

            {/* Edit column dialog (t8 issue #7). Pre-fills with the
                selected column's current values; saves via PATCH. */}
            <EditColumnDialog
                open={editingColumn !== null}
                onOpenChange={(open) => {
                    if (!open) setEditingColumnId(null);
                }}
                column={editingColumn}
                onSubmit={handleUpdateColumn}
                customFields={customFields}
                projectMembers={projectMembers}
                isLoading={updateColumn.isPending}
            />
        </div>
    );
}

function KanbanEmptyState({
    hasActiveFilters,
    onClearFilters,
}: {
    hasActiveFilters?: boolean;
    onClearFilters?: () => void;
}) {
    const { t } = useI18n();
    return (
        <div className="w-full flex flex-col items-center justify-center py-16 text-center">
            <div className="h-16 w-16 rounded-full bg-surface-hover flex items-center justify-center mb-4">
                <CheckSquare className="h-8 w-8 text-foreground-muted" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
                {hasActiveFilters ? t('tasks.noMatchingTasks') : t('tasks.noTasks')}
            </h3>
            <p className="text-foreground-muted mb-4 max-w-sm">
                {hasActiveFilters
                    ? t('tasks.tryAdjustingFilters')
                    : t('tasks.getStartedCreatingTask')}
            </p>
            {hasActiveFilters && onClearFilters && (
                <button onClick={onClearFilters} className="text-sm text-primary hover:underline">
                    {t('filters.clearAll')}
                </button>
            )}
        </div>
    );
}

// ============================================
// KANBAN NO-COLUMNS STATE
// ============================================
//
// R1: shown when a brand-new user-created Kanban view has zero stored
// columns. Carries the "Create your first column" CTA that opens the
// shared AddColumnDialog instance wired above. Visually mirrors
// KanbanEmptyState (CheckSquare glyph + title + description + button)
// so the two empty states feel consistent if a user lands on one and
// then the other (e.g. adds columns and clears all tasks).
function KanbanNoColumnsState({ onCreate }: { onCreate: () => void }) {
    const { t } = useI18n();
    return (
        <div className="w-full flex flex-col items-center justify-center py-16 text-center">
            <div className="h-16 w-16 rounded-full bg-surface-hover flex items-center justify-center mb-4">
                <CheckSquare className="h-8 w-8 text-foreground-muted" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
                {t('kanban.noColumnsTitle')}
            </h3>
            <p className="text-foreground-muted mb-4 max-w-sm">
                {t('kanban.noColumnsDescription')}
            </p>
            <Button
                variant="default"
                size="sm"
                onClick={onCreate}
                className="h-9 px-4"
            >
                <Plus className="h-4 w-4 mr-2" />
                {t('kanban.createFirstColumn')}
            </Button>
        </div>
    );
}
